import { EventPriceTier } from './types';

/**
 * Preisstufen der Anmeldung (V179) — Anzeige auf Event-Seite und in der App. Nur Fristen, keine
 * Beträge: wichtig ist, wann es teurer wird.
 *
 * Gerechnet wird auf dem Kalendertag, nicht auf der Uhrzeit: „bis 31.12." heißt der ganze Tag.
 */

function dayKey(date: Date): string {
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${m}-${d}`;
}

/** Nach Frist sortiert, wie das Backend speichert. */
export function sortPriceTiers(tiers: EventPriceTier[] | null | undefined): EventPriceTier[] {
  return [...(tiers ?? [])].sort((a, b) => a.validUntil.localeCompare(b.validUntil));
}

export type PriceTierState = 'past' | 'current' | 'upcoming';

export interface PriceTierRow {
  tier: EventPriceTier;
  /** „Frühbucher" oder, ohne Bezeichnung, „2. Preisstufe". */
  name: string;
  state: PriceTierState;
}

/** Jede Stufe mit ihrem Stand heute: abgelaufen, gilt gerade, kommt noch. */
export function priceTierRows(tiers: EventPriceTier[] | null | undefined, now: Date = new Date()): PriceTierRow[] {
  const today = dayKey(now);
  let currentSeen = false;
  return sortPriceTiers(tiers).map((tier, index) => {
    const name = tier.label || `${index + 1}. Preisstufe`;
    if (tier.validUntil < today) return { tier, name, state: 'past' as const };
    if (!currentSeen) {
      currentSeen = true;
      return { tier, name, state: 'current' as const };
    }
    return { tier, name, state: 'upcoming' as const };
  });
}

/** „bis 31.12.2026" — die Frist einer Stufe in Worten. */
export function priceTierUntilLabel(tier: EventPriceTier): string {
  const [y, m, d] = tier.validUntil.slice(0, 10).split('-');
  return `bis ${d}.${m}.${y}`;
}

/**
 * Der Hinweis unter den Preisstufen: wann es als Nächstes teurer wird. Dieselbe Regel wie
 * `nextPriceChange` im Backend. `null`, wenn alle Fristen vorbei sind.
 */
export function nextPriceChangeLabel(tiers: EventPriceTier[] | null | undefined, now: Date = new Date()): string | null {
  const current = priceTierRows(tiers, now).find(r => r.state === 'current');
  if (!current) return null;
  return `Ab dem ${dayAfter(current.tier.validUntil)} wird es teurer`;
}

function dayAfter(iso: string): string {
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  const next = new Date(y, m - 1, d + 1);
  return `${String(next.getDate()).padStart(2, '0')}.${String(next.getMonth() + 1).padStart(2, '0')}.`;
}
